'use client';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Skeleton from '@mui/material/Skeleton';
import type {Theme} from '@mui/material/styles';
import {Section} from '../primitives';
import {CardGridSkeleton} from './CardGridSkeleton';

// A row of FilterChip placeholders (radius 8px, ~40px tall) — the category
// filter the news listing renders above its grid. Off by default since the
// other listings (projects / countries / phases / unit-types) have no filter.
function FilterRowSkeleton() {
  return (
    <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3}}>
      {[64, 88, 72, 96, 80].map((w, i) => (
        <Skeleton key={i} variant="rounded" animation="wave" width={w} height={40} sx={theme => ({borderRadius: `${theme.radii.md}px`})} />
      ))}
    </Box>
  );
}

// The featured-project hero the all-projects listing renders above its heading
// (OrbitMediaBand): a full-bleed dark band pairing FEATURED
// eyebrow + name + divider + location + description + two CTAs with the circular
// orbit media. On the dark surface MUI's default (black) skeleton tint is
// invisible, so each placeholder is repainted with the inverse-subtle token the
// band itself uses. Mobile stacks the media above the copy (column-reverse).
const onDark = (theme: Theme) => theme.palette.colors.surface.neutral.inverseSubtle;

function FeaturedHeroSkeleton() {
  return (
    <Box sx={theme => ({backgroundColor: theme.palette.colors.surface.neutral.inverseMuted, overflow: 'hidden'})}>
      <Container>
        <Box
          sx={{
            display: 'flex',
            flexDirection: {xs: 'column-reverse', md: 'row'},
            alignItems: 'center',
            justifyContent: {md: 'space-between'},
            gap: {xs: 4, md: 6},
            paddingBlock: {xs: 5, md: 0},
          }}
        >
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.25, flex: {md: '1 1 0'}, minWidth: 0, maxWidth: 513, width: '100%'}}>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
              <Skeleton variant="text" animation="wave" width={96} sx={{fontSize: theme => theme.font.size['body-3'], bgcolor: onDark}} />
              <Skeleton variant="text" animation="wave" width="72%" sx={{fontSize: theme => theme.font.size['title-1'], bgcolor: onDark}} />
              <Skeleton variant="rectangular" animation="wave" width="100%" height={1} sx={{bgcolor: onDark}} />
            </Box>
            <Skeleton variant="text" animation="wave" width={160} sx={{bgcolor: onDark}} />
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
              <Skeleton variant="text" animation="wave" width="100%" sx={{bgcolor: onDark}} />
              <Skeleton variant="text" animation="wave" width="84%" sx={{bgcolor: onDark}} />
            </Box>
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
              <Skeleton
                variant="rounded"
                animation="wave"
                width={172}
                height={48}
                sx={theme => ({borderRadius: `${theme.radii.full}px`, bgcolor: onDark})}
              />
              <Skeleton
                variant="rounded"
                animation="wave"
                width={150}
                height={48}
                sx={theme => ({borderRadius: `${theme.radii.full}px`, bgcolor: onDark})}
              />
            </Box>
          </Box>
          <Box sx={{flexShrink: 0, width: '100%', maxWidth: {xs: 420, md: 512}}}>
            <Skeleton
              variant="circular"
              animation="wave"
              sx={{width: '100%', height: 'auto', aspectRatio: '1 / 1', mx: 'auto', bgcolor: onDark}}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

// Mirrors ListingPage (All Projects / Countries / Phases / Unit-Types / News):
// an optional featured hero (all-projects only), a left-aligned heading, an
// optional filter row (news), then the responsive card grid. No masthead — the
// real listing pages have no fixed-height banner.
export function ListingPageSkeleton({filter = false, featured = false}: {filter?: boolean; featured?: boolean}) {
  return (
    <>
      {featured && <FeaturedHeroSkeleton />}
      <Section>
        <Container>
          <Box sx={{mb: 3, display: 'flex', flexDirection: 'column', gap: 0.5}}>
            <Skeleton variant="text" animation="wave" width={240} sx={{fontSize: theme => theme.font.size['title-3']}} />
          </Box>
        </Container>
        <Container>
          {filter && <FilterRowSkeleton />}
          <CardGridSkeleton />
        </Container>
      </Section>
    </>
  );
}

ListingPageSkeleton.displayName = 'ListingPageSkeleton';
